import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  FaChevronLeft,
  FaChevronRight,
  FaExpand,
  FaFolderOpen,
  FaImage,
  FaPencilAlt,
  FaPlus,
  FaSpinner,
  FaTrashAlt,
  FaTimes,
  FaVideo,
} from 'react-icons/fa';
import { socialService } from '../../services/apiService';
import ConfirmDialog from '../ConfirmDialog';
import MentionTextarea from '../MentionTextarea';
import HubItemContentWithTranslate from '../HubItemTranslateControl';
import './DevHubGallery.css';

const EMPTY_CATEGORY = { title: '', description: '' };
const EMPTY_ITEM = {
  title: '',
  description: '',
  mediaAlt: '',
  mediaPlacement: 'top',
  currentlyWorking: false,
  startYear: '',
  endYear: '',
};
const MAX_ITEMS_PER_CATEGORY = 10;
const WORK_YEAR_START = 1990;

const MEDIA_PLACEMENT_VALUES = ['top', 'bottom', 'left', 'right'];

function getYearOptions() {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = currentYear + 1; year >= WORK_YEAR_START; year -= 1) {
    years.push(year);
  }
  return years;
}

function getPlacementOptions(t) {
  return MEDIA_PLACEMENT_VALUES.map((value) => ({
    value,
    label: t(`hub.placement.${value}.label`),
    hint: t(`hub.placement.${value}.hint`),
  }));
}

function normalizePlacement(value) {
  const placement = String(value || '').trim().toLowerCase();
  return MEDIA_PLACEMENT_VALUES.includes(placement)
    ? placement
    : 'top';
}

function formatMediaLabel(file) {
  if (!file) return '';
  const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
  return `${file.name} · ${sizeMb} MB`;
}

function MediaPreview({ media, title, large = false, onOpen = null }) {
  const { t } = useTranslation();

  if (!media?.url) {
    return (
      <div className={`hub-card-media${large ? ' is-large' : ''} is-empty`} aria-hidden="true">
        <FaImage />
      </div>
    );
  }

  const canOpen = Boolean(onOpen);
  const Wrapper = canOpen ? 'button' : 'div';
  const openLabelKey = media.resourceType === 'video' ? 'hub.openVideo' : 'hub.openImage';
  const wrapperProps = canOpen
    ? {
      type: 'button',
      className: `hub-card-media${large ? ' is-large' : ''} is-openable`,
      onClick: (event) => {
        event.stopPropagation();
        onOpen(media, title);
      },
      'aria-label': t(openLabelKey, { title: title || media.alt || t('hub.attachment') }),
    }
    : {
      className: `hub-card-media${large ? ' is-large' : ''}`,
    };

  if (media.resourceType === 'video') {
    return (
      <Wrapper {...wrapperProps}>
        <video
          src={media.url}
          muted
          playsInline
          preload="metadata"
          controls={!canOpen}
          aria-label={media.alt || title || t('hub.video')}
        />
        <span className="hub-card-media-badge"><FaVideo aria-hidden="true" /> {t('hub.video')}</span>
        {canOpen && (
          <span className="hub-card-media-expand" aria-hidden="true">
            <FaExpand />
          </span>
        )}
      </Wrapper>
    );
  }

  return (
    <Wrapper {...wrapperProps}>
      <img
        src={media.url}
        alt={media.alt || title || ''}
        loading="lazy"
        width={media.width || undefined}
        height={media.height || undefined}
      />
      {canOpen && (
        <span className="hub-card-media-expand" aria-hidden="true">
          <FaExpand />
        </span>
      )}
    </Wrapper>
  );
}

function MediaLightbox({ open, media, title, onClose }) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (typeof document === 'undefined' || !open || !media?.url) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        className="hub-lightbox-backdrop"
        role="presentation"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="hub-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={title || media.alt || t('hub.mediaView')}
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.97 }}
          transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
          onClick={(event) => event.stopPropagation()}
        >
          <header className="hub-lightbox-head">
            <div>
              <p className="hub-lightbox-kicker">{t('hub.eyebrow')}</p>
              <h3>{title || media.alt || t('hub.attachment')}</h3>
            </div>
            <button type="button" className="hub-icon-btn" onClick={onClose} aria-label={t('hub.close')}>
              <FaTimes />
            </button>
          </header>
          <div className="hub-lightbox-body">
            {media.resourceType === 'video' ? (
              <video
                src={media.url}
                controls
                autoPlay
                playsInline
                aria-label={media.alt || title || t('hub.video')}
              />
            ) : (
              <img
                src={media.url}
                alt={media.alt || title || ''}
              />
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

function CategoryCarousel({
  category,
  isOwner,
  busy,
  onEditItem,
  onDeleteItem,
  onOpenMedia,
}) {
  const { t } = useTranslation();
  const items = category.items || [];
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  useEffect(() => {
    setIndex((current) => {
      if (!items.length) return 0;
      return Math.min(current, items.length - 1);
    });
  }, [items.length, category.id]);

  if (!items.length) return null;

  const safeIndex = ((index % items.length) + items.length) % items.length;
  const item = items[safeIndex];
  const canNavigate = items.length > 1;
  const placement = normalizePlacement(item.mediaPlacement);

  const goTo = (nextIndex, dir) => {
    setDirection(dir);
    setIndex(((nextIndex % items.length) + items.length) % items.length);
  };

  const goPrev = () => goTo(safeIndex - 1, -1);
  const goNext = () => goTo(safeIndex + 1, 1);

  return (
    <div className="hub-carousel" aria-roledescription="carrossel">
      <div className="hub-carousel-stage">
        {canNavigate && (
          <button
            type="button"
            className="hub-carousel-nav is-prev"
            aria-label={t('hub.prevItem')}
            onClick={goPrev}
            disabled={busy}
          >
            <FaChevronLeft aria-hidden="true" />
          </button>
        )}

        <div className="hub-carousel-viewport">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.article
              key={item.id}
              className={`hub-feature-card is-placement-${placement}`}
              custom={direction}
              initial={{ opacity: 0, x: direction >= 0 ? 28 : -28 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: direction >= 0 ? -28 : 28 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              aria-roledescription="slide"
              aria-label={`${t('hub.carouselCount', { current: safeIndex + 1, total: items.length })}: ${item.title}`}
            >
              <div className="hub-feature-media">
                <MediaPreview
                  media={item.media}
                  title={item.title}
                  large
                  onOpen={item.media?.url ? onOpenMedia : null}
                />
              </div>
              <div className="hub-feature-copy">
                <HubItemContentWithTranslate item={item} />
                {isOwner && (
                  <div className="hub-feature-actions">
                    <button
                      type="button"
                      className="hub-icon-btn"
                      aria-label={t('hub.editItem')}
                      onClick={() => onEditItem(category, item)}
                      disabled={busy}
                    >
                      <FaPencilAlt />
                    </button>
                    <button
                      type="button"
                      className="hub-icon-btn is-danger"
                      aria-label={t('hub.deleteItem')}
                      onClick={() => onDeleteItem(category, item)}
                      disabled={busy}
                    >
                      <FaTrashAlt />
                    </button>
                  </div>
                )}
              </div>
            </motion.article>
          </AnimatePresence>
        </div>

        {canNavigate && (
          <button
            type="button"
            className="hub-carousel-nav is-next"
            aria-label={t('hub.nextItem')}
            onClick={goNext}
            disabled={busy}
          >
            <FaChevronRight aria-hidden="true" />
          </button>
        )}
      </div>

      {canNavigate && (
        <div className="hub-carousel-footer">
          <span className="hub-carousel-count">
            {t('hub.carouselCount', { current: safeIndex + 1, total: items.length })}
          </span>
          <div className="hub-carousel-dots" role="tablist" aria-label={t('hub.subPosts')}>
            {items.map((entry, dotIndex) => (
              <button
                key={entry.id}
                type="button"
                role="tab"
                aria-selected={dotIndex === safeIndex}
                aria-label={t('hub.goToItem', { title: entry.title })}
                className={`hub-carousel-dot${dotIndex === safeIndex ? ' is-active' : ''}`}
                onClick={() => goTo(dotIndex, dotIndex > safeIndex ? 1 : -1)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ModalShell({ open, title, kicker, onClose, children, footer = null, wide = false }) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="hub-modal-backdrop"
          role="presentation"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className={`hub-modal${wide ? ' is-wide' : ''}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="hub-modal-title"
            initial={{ opacity: 0, y: 18, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            onClick={(event) => event.stopPropagation()}
          >
            <header className="hub-modal-head">
              <div>
                {kicker && <p className="hub-modal-kicker">{kicker}</p>}
                <h3 id="hub-modal-title">{title}</h3>
              </div>
              <button type="button" className="hub-icon-btn" onClick={onClose} aria-label={t('hub.close')}>
                <FaTimes />
              </button>
            </header>
            <div className="hub-modal-body">
              {children}
            </div>
            {footer ? (
              <footer className="hub-modal-footer">
                {footer}
              </footer>
            ) : null}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

export default function DevHubGallery({
  displayName,
  isOwner = false,
  categories: initialCategories = [],
  onCategoriesChange,
  onStatus,
}) {
  const { t } = useTranslation();
  const [categories, setCategories] = useState(initialCategories);
  const [busy, setBusy] = useState(false);
  const [categoryModal, setCategoryModal] = useState(null);
  const [itemModal, setItemModal] = useState(null);
  const [categoryForm, setCategoryForm] = useState(EMPTY_CATEGORY);
  const [itemForm, setItemForm] = useState(EMPTY_ITEM);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const fileInputRef = useRef(null);
  const placementOptions = useMemo(() => getPlacementOptions(t), [t]);
  const yearOptions = useMemo(() => getYearOptions(), []);

  useEffect(() => {
    setCategories(initialCategories);
  }, [initialCategories]);

  useEffect(() => {
    if (!mediaFile) {
      setMediaPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(mediaFile);
    setMediaPreview({
      url,
      resourceType: mediaFile.type.startsWith('video/') ? 'video' : 'image',
    });
    return () => URL.revokeObjectURL(url);
  }, [mediaFile]);

  const totalItems = useMemo(
    () => categories.reduce((sum, category) => sum + (category.items?.length || 0), 0),
    [categories]
  );

  const syncCategories = (next) => {
    setCategories(next);
    onCategoriesChange?.(next);
  };

  const openCreateCategory = () => {
    setCategoryForm(EMPTY_CATEGORY);
    setCategoryModal({ mode: 'create' });
  };

  const openEditCategory = (category) => {
    setCategoryForm({
      title: category.title || '',
      description: category.description || '',
    });
    setCategoryModal({ mode: 'edit', category });
  };

  const openCreateItem = (category) => {
    if ((category.items?.length || 0) >= MAX_ITEMS_PER_CATEGORY) {
      onStatus?.({
        type: 'error',
        message: t('hub.maxItems', { count: MAX_ITEMS_PER_CATEGORY }),
      });
      return;
    }
    setItemForm(EMPTY_ITEM);
    setMediaFile(null);
    setItemModal({ mode: 'create', category });
  };

  const openEditItem = (category, item) => {
    setItemForm({
      title: item.title || '',
      description: item.description || '',
      mediaAlt: item.media?.alt || '',
      mediaPlacement: normalizePlacement(item.mediaPlacement),
      currentlyWorking: Boolean(item.currentlyWorking),
      startYear: item.startYear ? String(item.startYear) : '',
      endYear: item.endYear ? String(item.endYear) : '',
    });
    setMediaFile(null);
    setItemModal({ mode: 'edit', category, item });
  };

  const closeModals = () => {
    if (busy) return;
    setCategoryModal(null);
    setItemModal(null);
    setMediaFile(null);
  };

  const handleSaveCategory = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      if (categoryModal?.mode === 'edit' && categoryModal.category) {
        const res = await socialService.updateHubCategory(categoryModal.category.id, categoryForm);
        const updated = res.data.category;
        syncCategories(categories.map((category) => (
          category.id === updated.id ? { ...category, ...updated, items: category.items } : category
        )));
        onStatus?.({ type: 'success', message: t('hub.categoryUpdated') });
      } else {
        const res = await socialService.createHubCategory(categoryForm);
        syncCategories([...categories, res.data.category]);
        onStatus?.({ type: 'success', message: t('hub.categoryCreated') });
      }
      setCategoryModal(null);
    } catch (err) {
      onStatus?.({ type: 'error', message: err.message });
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteCategory = (category) => {
    if (busy) return;
    setConfirmDialog({
      type: 'category',
      category,
      title: t('hub.deleteCategoryTitle'),
      message: t('hub.deleteCategoryMessage', { title: category.title }),
      confirmLabel: t('hub.deleteCategoryConfirm'),
    });
  };

  const handleSaveItem = async (event) => {
    event.preventDefault();
    if (busy || !itemModal?.category) return;
    setBusy(true);
    try {
      const payload = {
        title: itemForm.title,
        description: itemForm.description,
        mediaAlt: itemForm.mediaAlt,
        mediaPlacement: itemForm.mediaPlacement,
        currentlyWorking: Boolean(itemForm.currentlyWorking),
        startYear: itemForm.startYear ? String(itemForm.startYear) : '',
        endYear: itemForm.currentlyWorking
          ? ''
          : (itemForm.endYear ? String(itemForm.endYear) : ''),
        media: mediaFile || undefined,
      };

      if (itemModal.mode === 'edit' && itemModal.item) {
        const res = await socialService.updateHubItem(itemModal.item.id, payload);
        const updated = {
          ...res.data.item,
          currentlyWorking: Boolean(
            res.data.item?.currentlyWorking ?? payload.currentlyWorking
          ),
          startYear: res.data.item?.startYear ?? (payload.startYear ? Number(payload.startYear) : null),
          endYear: res.data.item?.endYear ?? (
            payload.currentlyWorking || !payload.endYear ? null : Number(payload.endYear)
          ),
        };
        syncCategories(categories.map((category) => {
          if (category.id !== itemModal.category.id) return category;
          return {
            ...category,
            items: (category.items || []).map((item) => (
              item.id === updated.id ? updated : item
            )),
            itemsCount: category.itemsCount,
          };
        }));
        onStatus?.({ type: 'success', message: t('hub.itemUpdated') });
      } else {
        const res = await socialService.createHubItem(itemModal.category.id, payload);
        const created = {
          ...res.data.item,
          currentlyWorking: Boolean(
            res.data.item?.currentlyWorking ?? payload.currentlyWorking
          ),
          startYear: res.data.item?.startYear ?? (payload.startYear ? Number(payload.startYear) : null),
          endYear: res.data.item?.endYear ?? (
            payload.currentlyWorking || !payload.endYear ? null : Number(payload.endYear)
          ),
        };
        syncCategories(categories.map((category) => {
          if (category.id !== itemModal.category.id) return category;
          const items = [...(category.items || []), created];
          return { ...category, items, itemsCount: items.length };
        }));
        onStatus?.({ type: 'success', message: t('hub.itemPublished') });
      }
      setItemModal(null);
      setMediaFile(null);
    } catch (err) {
      onStatus?.({ type: 'error', message: err.message });
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteItem = (category, item) => {
    if (busy) return;
    setConfirmDialog({
      type: 'item',
      category,
      item,
      title: t('hub.deleteItemTitle'),
      message: t('hub.deleteItemMessage', { title: item.title }),
      confirmLabel: t('hub.deleteItemConfirm'),
    });
  };

  const closeConfirmDialog = () => {
    if (busy) return;
    setConfirmDialog(null);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDialog || busy) return;
    setBusy(true);
    try {
      if (confirmDialog.type === 'category') {
        await socialService.deleteHubCategory(confirmDialog.category.id);
        syncCategories(categories.filter((item) => item.id !== confirmDialog.category.id));
        onStatus?.({ type: 'success', message: t('hub.categoryRemoved') });
      } else if (confirmDialog.type === 'item') {
        await socialService.deleteHubItem(confirmDialog.item.id);
        syncCategories(categories.map((entry) => {
          if (entry.id !== confirmDialog.category.id) return entry;
          const items = (entry.items || []).filter((row) => row.id !== confirmDialog.item.id);
          return { ...entry, items, itemsCount: items.length };
        }));
        onStatus?.({ type: 'success', message: t('hub.itemRemoved') });
      }
      setConfirmDialog(null);
    } catch (err) {
      onStatus?.({ type: 'error', message: err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="social-card hub-gallery-card">
      <div className="social-card-head hub-gallery-head">
        <div>
          <span className="social-card-eyebrow">{t('hub.eyebrow')}</span>
          <h2>{t('hub.portfolio')}</h2>
          <p className="hub-gallery-lead">
            {t('hub.lead')}
          </p>
        </div>
        <div className="hub-gallery-head-actions">
          <span className="profile-post-count" title={t('hub.subPosts')}>
            {totalItems}
          </span>
          {isOwner && (
            <button type="button" className="social-btn" onClick={openCreateCategory} disabled={busy}>
              <FaPlus aria-hidden="true" /> {t('hub.newCategory')}
            </button>
          )}
        </div>
      </div>

      {categories.length === 0 ? (
        <div className="social-empty-state hub-empty">
          <span className="social-empty-icon"><FaFolderOpen /></span>
          <strong>
            {isOwner ? t('hub.emptyOwnerTitle') : t('hub.emptyGuestTitle')}
          </strong>
          <p>
            {isOwner
              ? t('hub.emptyOwnerText')
              : t('hub.emptyGuestText', { name: displayName })}
          </p>
          {isOwner && (
            <button type="button" className="social-btn" onClick={openCreateCategory}>
              <FaPlus aria-hidden="true" /> {t('hub.createFirstCategory')}
            </button>
          )}
        </div>
      ) : (
        <div className="hub-category-stack">
          {categories.map((category, index) => (
            <motion.section
              key={category.id}
              className="hub-category"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: Math.min(index * 0.05, 0.25) }}
            >
              <header className="hub-category-head">
                <div>
                  <h3>{category.title}</h3>
                  {category.description?.trim() && (
                    <p>{category.description}</p>
                  )}
                </div>
                {isOwner && (
                  <div className="hub-category-actions">
                    <button
                      type="button"
                      className="social-btn social-btn-ghost hub-btn-compact"
                      onClick={() => openCreateItem(category)}
                      disabled={busy}
                    >
                      <FaPlus aria-hidden="true" /> {t('hub.subPost')}
                    </button>
                    <button
                      type="button"
                      className="hub-icon-btn"
                      aria-label={t('hub.editCategory')}
                      onClick={() => openEditCategory(category)}
                      disabled={busy}
                    >
                      <FaPencilAlt />
                    </button>
                    <button
                      type="button"
                      className="hub-icon-btn is-danger"
                      aria-label={t('hub.deleteCategory')}
                      onClick={() => handleDeleteCategory(category)}
                      disabled={busy}
                    >
                      <FaTrashAlt />
                    </button>
                  </div>
                )}
              </header>

              {(category.items || []).length === 0 ? (
                <div className="hub-category-empty">
                  <p>{t('hub.emptyCategory')}</p>
                  {isOwner && (
                    <button
                      type="button"
                      className="social-btn social-btn-ghost hub-btn-compact"
                      onClick={() => openCreateItem(category)}
                    >
                      <FaPlus aria-hidden="true" /> {t('hub.add')}
                    </button>
                  )}
                </div>
              ) : (
                <CategoryCarousel
                  category={category}
                  isOwner={isOwner}
                  busy={busy}
                  onEditItem={openEditItem}
                  onDeleteItem={handleDeleteItem}
                  onOpenMedia={(media, title) => setLightbox({ media, title })}
                />
              )}
            </motion.section>
          ))}
        </div>
      )}

      <ModalShell
        open={Boolean(categoryModal)}
        title={categoryModal?.mode === 'edit' ? t('hub.editCategoryTitle') : t('hub.newCategoryTitle')}
        kicker={t('hub.eyebrow')}
        onClose={closeModals}
        footer={(
          <div className="hub-form-actions">
            <button type="button" className="social-btn social-btn-ghost" onClick={closeModals} disabled={busy}>
              {t('hub.cancel')}
            </button>
            <button
              type="submit"
              form="hub-category-form"
              className="social-btn"
              disabled={busy || !categoryForm.title.trim()}
            >
              {busy ? <FaSpinner className="social-spin" /> : null}
              {categoryModal?.mode === 'edit' ? t('hub.save') : t('hub.createCategory')}
            </button>
          </div>
        )}
      >
        <form id="hub-category-form" className="hub-form" onSubmit={handleSaveCategory}>
          <label className="hub-field">
            <span>{t('hub.categoryTitle')}</span>
            <input
              type="text"
              maxLength={80}
              required
              placeholder={t('hub.categoryTitlePlaceholder')}
              value={categoryForm.title}
              onChange={(event) => setCategoryForm((current) => ({
                ...current,
                title: event.target.value,
              }))}
            />
          </label>
          <label className="hub-field">
            <span>{t('hub.categoryDescription')}</span>
            <textarea
              rows={3}
              maxLength={300}
              placeholder={t('hub.categoryDescriptionPlaceholder')}
              value={categoryForm.description}
              onChange={(event) => setCategoryForm((current) => ({
                ...current,
                description: event.target.value,
              }))}
            />
          </label>
        </form>
      </ModalShell>

      <ModalShell
        open={Boolean(itemModal)}
        title={itemModal?.mode === 'edit' ? t('hub.editItemTitle') : t('hub.newItemTitle')}
        kicker={itemModal?.category?.title || t('hub.eyebrow')}
        onClose={closeModals}
        wide
        footer={(
          <div className="hub-form-actions">
            <button type="button" className="social-btn social-btn-ghost" onClick={closeModals} disabled={busy}>
              {t('hub.cancel')}
            </button>
            <button
              type="submit"
              form="hub-item-form"
              className="social-btn"
              disabled={busy || !itemForm.title.trim()}
            >
              {busy ? <FaSpinner className="social-spin" /> : null}
              {itemModal?.mode === 'edit' ? t('hub.save') : t('hub.publish')}
            </button>
          </div>
        )}
      >
        <form id="hub-item-form" className="hub-form" onSubmit={handleSaveItem}>
          <label className="hub-field">
            <span>{t('hub.itemTitle')}</span>
            <input
              type="text"
              maxLength={100}
              required
              placeholder={t('hub.itemTitlePlaceholder')}
              value={itemForm.title}
              onChange={(event) => setItemForm((current) => ({
                ...current,
                title: event.target.value,
              }))}
            />
          </label>
          <div className="hub-field">
            <span>{t('hub.itemDescription')}</span>
            <MentionTextarea
              value={itemForm.description}
              onChange={(nextValue) => setItemForm((current) => ({
                ...current,
                description: nextValue.slice(0, 1000),
              }))}
              rows={3}
              maxLength={1000}
              placeholder={t('hub.itemDescriptionPlaceholder')}
              disabled={busy}
              className="hub-mention-textarea"
            />
          </div>

          <fieldset className="hub-work-period-field">
            <legend>{t('hub.workPeriod.label')}</legend>
            <p className="hub-work-period-hint">{t('hub.workPeriod.hint')}</p>

            <label className={`hub-work-toggle${itemForm.currentlyWorking ? ' is-active' : ''}`}>
              <input
                type="checkbox"
                checked={Boolean(itemForm.currentlyWorking)}
                onChange={(event) => setItemForm((current) => ({
                  ...current,
                  currentlyWorking: event.target.checked,
                  endYear: event.target.checked ? '' : current.endYear,
                }))}
              />
              <span className="hub-work-toggle-ui" aria-hidden="true" />
              <span className="hub-work-toggle-copy">
                <strong>{t('hub.workPeriod.stillWorking')}</strong>
                <small>{t('hub.workPeriod.stillWorkingHint')}</small>
              </span>
            </label>

            <div className="hub-work-year-grid">
              <label className="hub-field">
                <span>{t('hub.workPeriod.startYear')}</span>
                <select
                  value={itemForm.startYear}
                  onChange={(event) => setItemForm((current) => ({
                    ...current,
                    startYear: event.target.value,
                  }))}
                >
                  <option value="">{t('hub.workPeriod.yearOptional')}</option>
                  {yearOptions.map((year) => (
                    <option key={year} value={String(year)}>{year}</option>
                  ))}
                </select>
              </label>
              <label className={`hub-field${itemForm.currentlyWorking ? ' is-disabled' : ''}`}>
                <span>{t('hub.workPeriod.endYear')}</span>
                <select
                  value={itemForm.currentlyWorking ? '' : itemForm.endYear}
                  disabled={Boolean(itemForm.currentlyWorking)}
                  onChange={(event) => setItemForm((current) => ({
                    ...current,
                    endYear: event.target.value,
                  }))}
                >
                  <option value="">
                    {itemForm.currentlyWorking
                      ? t('hub.workPeriod.present')
                      : t('hub.workPeriod.yearOptional')}
                  </option>
                  {!itemForm.currentlyWorking && yearOptions.map((year) => (
                    <option key={year} value={String(year)}>{year}</option>
                  ))}
                </select>
              </label>
            </div>
          </fieldset>

          <fieldset className="hub-placement-field">
            <legend>{t('hub.mediaPlacement')}</legend>
            <p className="hub-placement-hint">
              {t('hub.mediaPlacementHint')}
            </p>
            <div className="hub-placement-grid" role="radiogroup" aria-label={t('hub.mediaPlacement')}>
              {placementOptions.map((option) => (
                <label
                  key={option.value}
                  className={`hub-placement-option${itemForm.mediaPlacement === option.value ? ' is-active' : ''}`}
                >
                  <input
                    type="radio"
                    name="mediaPlacement"
                    value={option.value}
                    checked={itemForm.mediaPlacement === option.value}
                    onChange={() => setItemForm((current) => ({
                      ...current,
                      mediaPlacement: option.value,
                    }))}
                  />
                  <span className={`hub-placement-preview is-${option.value}`} aria-hidden="true">
                    <span className="hub-placement-block is-media" />
                    <span className="hub-placement-block is-text" />
                  </span>
                  <span className="hub-placement-copy">
                    <strong>{option.label}</strong>
                    <small>{option.hint}</small>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="hub-media-field">
            <div className="hub-media-field-copy">
              <strong>{t('hub.mediaAttachment')}</strong>
              <p>{t('hub.mediaFormats')}</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0] || null;
                setMediaFile(file);
                event.target.value = '';
              }}
            />
            <button
              type="button"
              className="social-btn social-btn-ghost"
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
            >
              <FaImage aria-hidden="true" /> {t('hub.chooseFile')}
            </button>
            {(mediaPreview || itemModal?.item?.media) && (
              <div className="hub-media-preview">
                <MediaPreview
                  media={mediaPreview || itemModal?.item?.media}
                  title={itemForm.title}
                />
                <div className="hub-media-preview-meta">
                  {mediaFile ? (
                    <small>{formatMediaLabel(mediaFile)}</small>
                  ) : (
                    <small>{t('hub.currentMedia')}</small>
                  )}
                  {mediaFile && (
                    <button
                      type="button"
                      className="hub-text-btn"
                      onClick={() => setMediaFile(null)}
                    >
                      {t('hub.removeSelection')}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          <label className="hub-field">
            <span>{t('hub.mediaAlt')}</span>
            <input
              type="text"
              maxLength={180}
              placeholder={t('hub.mediaAltPlaceholder')}
              value={itemForm.mediaAlt}
              onChange={(event) => setItemForm((current) => ({
                ...current,
                mediaAlt: event.target.value,
              }))}
            />
          </label>
        </form>
      </ModalShell>

      <ConfirmDialog
        open={Boolean(confirmDialog)}
        title={confirmDialog?.title || t('hub.confirmAction')}
        message={confirmDialog?.message || ''}
        confirmLabel={confirmDialog?.confirmLabel || t('hub.confirm')}
        cancelLabel={t('hub.cancel')}
        tone="danger"
        busy={busy}
        onCancel={closeConfirmDialog}
        onConfirm={handleConfirmDelete}
      />

      <MediaLightbox
        open={Boolean(lightbox)}
        media={lightbox?.media}
        title={lightbox?.title}
        onClose={() => setLightbox(null)}
      />
    </section>
  );
}
